import { gql } from "graphql-request";

export const GET_COURSES = gql`
  query GetCourses {
    courses {
      id
      title
      slug
      description
      thumbnailUrl
      ogImageUrl
      difficultyLevel
      category
      subcategory
      estimatedHours
      isPublished
      publishStatus
      isFree
      totalLessons
      enrollmentCount
    }
  }
`;

// Track cards need lesson totals, but never render learner counts. Keep the
// remaining canonical course-list fields so the loader can apply the same
// publication and curriculum-activation checks as the shared list cache.
export const GET_TRACK_COURSES = gql`
  query GetTrackCourses {
    courses {
      id
      title
      slug
      description
      thumbnailUrl
      ogImageUrl
      difficultyLevel
      category
      subcategory
      estimatedHours
      isPublished
      publishStatus
      isFree
      totalLessons
    }
  }
`;

export const GET_COURSE_DETAIL = gql`
  query GetCourseDetail($slug: String!) {
    courseDetail(slug: $slug) {
      id
      title
      description
      slug
      thumbnailUrl
      ogImageUrl
      difficultyLevel
      category
      estimatedHours
      isPublished
      publishStatus
      isFree
      totalLessons
      enrollmentCount
      goal
      outcomes {
        text
        highlight
        icon
      }
      recommendedFor {
        title
        subtitle
        illustration
      }
      notRecommendedFor
      requiredKnowledge
      recommendedKnowledge
      environmentType
      environmentDetails {
        browser
        tools
      }
      faqs {
        question
        answer
      }
      sections {
        id
        title
        description
        orderIndex
        lessons {
          id
          title
          shortTitle
          slug
          description
          orderIndex
          duration
          type
          isFree
        }
      }
    }
  }
`;

// Lesson routes need only this narrow course/navigation projection plus one
// exact lesson body. Keep enrollmentCount and all sibling body/answer/code
// fields out of this operation: their resolvers/columns add work or can carry
// protected material that the sidebar never uses.
export const GET_PUBLIC_LESSON_PAGE = gql`
  query GetPublicLessonPage($courseSlug: String!, $lessonSlug: String!) {
    publicLessonPage(courseSlug: $courseSlug, lessonSlug: $lessonSlug) {
      course {
        id
        title
        slug
        description
        thumbnailUrl
        ogImageUrl
        difficultyLevel
        isPublished
        publishStatus
        # コース分割の活性化ゲートが照合する 4 つ。ここを外すと
        # linuc/server/cs のレッスンが全部 503 になる (2026-09-04 の障害)。
        # 判定側は「欠けている」と「値が違う」を区別できないので、
        # 選択から漏れた時点で fail-closed に倒れる
        totalLessons
        isFree
        category
        subcategory
        sections {
          id
          title
          description
          orderIndex
          lessons {
            id
            title
            shortTitle
            slug
            description
            orderIndex
            duration
            type
            isFree
          }
        }
      }
      lesson {
        id
        sectionId
        title
        seoTitle
        seoDescription
        slug
        description
        orderIndex
        type
        isFree
        duration
        articleContent
        videoUrl
        codingProblemId
        faqs {
          question
          answer
        }
        referenceLinks
        miniQuizQuestion
        miniQuizChoices {
          choices {
            id
            text
            isCorrect
          }
          correctExplanation
          incorrectExplanation
        }
        createdAt
        updatedAt
      }
    }
  }
`;

export const GET_RELATED_COURSES = gql`
  query GetRelatedCourses($courseSlug: String!) {
    relatedCourses(courseSlug: $courseSlug) {
      prerequisites {
        id
        description
        course {
          id
          title
          slug
          description
          thumbnailUrl
          difficultyLevel
          totalLessons
        }
      }
      nextSteps {
        id
        description
        course {
          id
          title
          slug
          description
          thumbnailUrl
          difficultyLevel
          totalLessons
        }
      }
      related {
        id
        description
        course {
          id
          title
          slug
          description
          thumbnailUrl
          difficultyLevel
          totalLessons
        }
      }
    }
  }
`;

export const GET_TRACK_COURSE_BUNDLE = gql`
  query GetTrackCourseBundle($courseSlugs: [String!]!) {
    trackCourseBundle(courseSlugs: $courseSlugs) {
      courseSlug
      lessons {
        id
        slug
      }
      related {
        id
        relationType
        orderIndex
        description
        course {
          id
          title
          slug
          thumbnailUrl
        }
      }
    }
  }
`;

export const GET_ROADMAP = gql`
  query GetRoadmap {
    courses {
      id
      title
      slug
      publishStatus
      updatedAt
      thumbnailUrl
      sections {
        id
        title
        orderIndex
        lessons {
          id
          title
          slug
          orderIndex
          duration
          type
          isFree
          updatedAt
        }
      }
    }
  }
`;

const JOB_FIELDS = `
  id
  slug
  title
  thumbnailUrl
  type
  category
  experienceLevel
  location
  featured
  description
  skills
  postedAt
  deadline
  views
  applications
  company {
    id
    companyId
    slug
    name
    logo
    website
    description
    size
    industry
    location
    claimStatus
    descriptionMode
    corporateDomain
    careersUrl
    lastVerifiedAt
    authorizationScopes
  }
  salary {
    min
    max
    currency
    period
  }
  requirements {
    category
    items
  }
  benefits {
    icon
    title
    description
  }
  platformRequirements {
    type
    name
    value
    required
    description
  }
  companyType
  workStyle
  workingHours
  appealPoints
  salaryLevel
  growthRate
  turnoverRate
  specialTags
  lifecycleStatus
  authorizationStatus
  authorizationScopes
  contentDisplayMode
  canonicalSourceUrl
  sourceDestinationType
  sourceCtaMode
  sourceUrlHealthy
  officialApplyUrl
  officialApplyUrlHealthy
  lastVerifiedAt
  sourceName
  sourceType
  verificationIntervalHours
  qualityScore
  uniqueValueScore
  learningGraphScore
  seoIndexStatus
  structuredDataStatus
  duplicateOfJobId
  coreFieldsComplete
  visibleContentMatchesSchema
`;

const JOB_SUMMARY_FIELDS = `
  id
  slug
  title
  thumbnailUrl
  type
  category
  experienceLevel
  location
  featured
  skills
  postedAt
  company {
    id
    slug
    name
    logo
    claimStatus
    descriptionMode
    authorizationScopes
  }
  salary {
    min
    max
    currency
    period
  }
  platformRequirements {
    required
  }
  companyType
  workStyle
  workingHours
  appealPoints
  salaryLevel
  growthRate
  turnoverRate
  specialTags
  lifecycleStatus
  authorizationStatus
  authorizationScopes
  contentDisplayMode
  lastVerifiedAt
  duplicateOfJobId
`;

export const GET_JOBS = gql`
  query GetJobs(
    $companySlug: String
    $type: String
    $category: String
    $experienceLevel: String
    $location: String
    $featured: Boolean
    $limit: Int
    $offset: Int
  ) {
    jobs(
      companySlug: $companySlug
      type: $type
      category: $category
      experienceLevel: $experienceLevel
      location: $location
      featured: $featured
      limit: $limit
      offset: $offset
    ) {
      ${JOB_FIELDS}
    }
  }
`;

export const GET_JOB_SUMMARIES = gql`
  query GetJobSummaries(
    $companySlug: String
    $type: String
    $category: String
    $experienceLevel: String
    $location: String
    $featured: Boolean
    $limit: Int
    $offset: Int
  ) {
    jobs(
      companySlug: $companySlug
      type: $type
      category: $category
      experienceLevel: $experienceLevel
      location: $location
      featured: $featured
      limit: $limit
      offset: $offset
    ) {
      ${JOB_SUMMARY_FIELDS}
    }
  }
`;

export const GET_JOB_DETAIL = gql`
  query GetJob($id: Int, $slug: String) {
    job(id: $id, slug: $slug) {
      ${JOB_FIELDS}
    }
  }
`;

// Companies
export const GET_COMPANIES = gql`
  query GetCompanies {
    companies {
      id
      companyId
      slug
      name
      logo
      website
      description
      size
      industry
      location
      claimStatus
      descriptionMode
      corporateDomain
      careersUrl
      lastVerifiedAt
      authorizationScopes
      createdAt
      updatedAt
    }
  }
`;

export const GET_COMPANY_SUMMARIES = gql`
  query GetCompanySummaries {
    companies {
      id
      slug
      name
      logo
      description
      size
      industry
      location
      claimStatus
      descriptionMode
      authorizationScopes
    }
  }
`;

export const GET_COMPANY = gql`
  query GetCompany($id: Int, $slug: String) {
    company(id: $id, slug: $slug) {
      id
      companyId
      slug
      name
      logo
      website
      description
      size
      industry
      location
      claimStatus
      descriptionMode
      corporateDomain
      careersUrl
      lastVerifiedAt
      authorizationScopes
      createdAt
      updatedAt
    }
  }
`;

export const GET_COMPANY_WITH_JOB_SUMMARIES = gql`
  query GetCompanyWithJobSummaries($slug: String!) {
    company(slug: $slug) {
      id
      companyId
      slug
      name
      logo
      website
      description
      size
      industry
      location
      claimStatus
      descriptionMode
      authorizationScopes
      createdAt
      updatedAt
    }
    jobs(companySlug: $slug) {
      ${JOB_SUMMARY_FIELDS}
    }
  }
`;

export const CREATE_COMPANY = gql`
  mutation CreateCompany($input: CompanyInput!) {
    createCompany(input: $input) {
      id
      companyId
      slug
      name
      logo
      website
      description
      size
      industry
      location
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_COMPANY = gql`
  mutation UpdateCompany($id: Int!, $input: CompanyUpdateInput!) {
    updateCompany(id: $id, input: $input) {
      id
      companyId
      slug
      name
      logo
      website
      description
      size
      industry
      location
      createdAt
      updatedAt
    }
  }
`;

export const DELETE_COMPANY = gql`
  mutation DeleteCompany($id: Int!) {
    deleteCompany(id: $id)
  }
`;

// Users
export const GET_USERS = gql`
  query GetUsers {
    users {
      id
      email
      name
      role
      avatarUrl
      emailVerified
      lastLogin
      referralSource
      lineId
      birthDate
      usagePurpose
      learningLevel
      isActive
      stripeCustomerId
      createdAt
      updatedAt
    }
  }
`;

export const GET_USER_COUNT = gql`
  query GetUserCount {
    userCount
  }
`;

export const GET_USER = gql`
  query GetUser($id: String!) {
    user(id: $id) {
      id
      email
      name
      role
      avatarUrl
      emailVerified
      lastLogin
      referralSource
      lineId
      birthDate
      usagePurpose
      learningLevel
      isActive
      stripeCustomerId
      createdAt
      updatedAt
    }
  }
`;

export const CREATE_USER = gql`
  mutation CreateUser($input: UserInput!) {
    createUser(input: $input) {
      id
      email
      name
      role
      avatarUrl
      emailVerified
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_USER = gql`
  mutation UpdateUser($id: String!, $input: UserUpdateInput!) {
    updateUser(id: $id, input: $input) {
      id
      email
      name
      role
      avatarUrl
      emailVerified
      lastLogin
      referralSource
      lineId
      birthDate
      usagePurpose
      learningLevel
      isActive
      stripeCustomerId
      createdAt
      updatedAt
    }
  }
`;

export const DELETE_USER = gql`
  mutation DeleteUser($id: String!) {
    deleteUser(id: $id)
  }
`;

// Courses (additional mutations)
export const CREATE_COURSE = gql`
  mutation CreateCourse($input: CreateCourseInput!) {
    createCourse(input: $input) {
      id
      title
      description
      slug
      thumbnailUrl
      difficultyLevel
      estimatedHours
      isPublished
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_COURSE = gql`
  mutation UpdateCourse($input: UpdateCourseInput!) {
    updateCourse(input: $input) {
      id
      title
      description
      slug
      thumbnailUrl
      ogImageUrl
      difficultyLevel
      category
      estimatedHours
      isPublished
      createdAt
      updatedAt
    }
  }
`;

export const DELETE_COURSE = gql`
  mutation DeleteCourse($id: String!) {
    deleteCourse(id: $id)
  }
`;

// Sections
export const GET_SECTIONS = gql`
  query GetSections($courseId: String) {
    sections(courseId: $courseId) {
      id
      courseId
      title
      description
      orderIndex
      createdAt
      updatedAt
    }
  }
`;

export const GET_SECTION = gql`
  query GetSection($id: String!) {
    section(id: $id) {
      id
      courseId
      title
      description
      orderIndex
      createdAt
      updatedAt
    }
  }
`;

export const CREATE_SECTION = gql`
  mutation CreateSection($input: CreateSectionInput!) {
    createSection(input: $input) {
      id
      courseId
      title
      description
      orderIndex
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_SECTION = gql`
  mutation UpdateSection($input: UpdateSectionInput!) {
    updateSection(input: $input) {
      id
      courseId
      title
      description
      orderIndex
      createdAt
      updatedAt
    }
  }
`;

export const DELETE_SECTION = gql`
  mutation DeleteSection($id: String!) {
    deleteSection(id: $id)
  }
`;

// Lessons
export const GET_LESSONS = gql`
  query GetLessons($sectionId: String) {
    lessons(sectionId: $sectionId) {
      id
      sectionId
      title
      slug
      description
      orderIndex
      type
      isFree
      duration
      createdAt
      updatedAt
    }
  }
`;

export const GET_LESSON = gql`
  query GetLesson($id: String!) {
    lesson(id: $id) {
      id
      sectionId
      title
      seoTitle
      seoDescription
      slug
      description
      orderIndex
      type
      isFree
      duration
      articleContent
      videoUrl
      codingProblemId
      faqs {
        question
        answer
      }
      referenceLinks
      miniQuizQuestion
      miniQuizChoices {
        choices {
          id
          text
          isCorrect
        }
        correctExplanation
        incorrectExplanation
      }
      createdAt
      updatedAt
    }
  }
`;

export const CREATE_LESSON = gql`
  mutation CreateLesson($input: CreateLessonInput!) {
    createLesson(input: $input) {
      id
      sectionId
      title
      slug
      description
      orderIndex
      type
      isFree
      duration
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_LESSON = gql`
  mutation UpdateLesson($input: UpdateLessonInput!) {
    updateLesson(input: $input) {
      id
      sectionId
      title
      slug
      description
      orderIndex
      type
      isFree
      duration
      articleContent
      videoUrl
      faqs {
        question
        answer
      }
      createdAt
      updatedAt
    }
  }
`;

export const DELETE_LESSON = gql`
  mutation DeleteLesson($id: String!) {
    deleteLesson(id: $id)
  }
`;

// Coding Problems
export const GET_CODING_PROBLEM = gql`
  query GetCodingProblem($id: String!) {
    codingProblem(id: $id) {
      id
      title
      difficulty
      language
      description
      requirements
      functionName
      inputSpec
      outputSpec
      testCases
      hints
      starterCode
      solution
      files
      template
      instructions
    }
  }
`;

// Learner-safe projection: authoritative tests and solutions are deliberately
// absent. exerciseSpec contains only browser-native checks/configuration.
export const GET_CODING_PROBLEM_LEARNER_VIEW = gql`
  query GetCodingProblemLearnerView($lessonId: String!) {
    codingProblemLearnerView(lessonId: $lessonId) {
      problem {
        id
        title
        difficulty
        language
        description
        requirements
        functionName
        inputSpec
        outputSpec
        hints
        starterCode
        files
        template
        instructions
      }
      exerciseSpec
    }
  }
`;

export const GET_CODING_PROBLEM_SOLUTION = gql`
  query GetCodingProblemSolution($lessonId: String!) {
    codingProblemSolution(lessonId: $lessonId)
  }
`;

// Coding Problem Mutations
export const CREATE_CODING_PROBLEM = gql`
  mutation CreateCodingProblem($input: CreateCodingProblemInput!) {
    createCodingProblem(input: $input) {
      id
      title
      difficulty
      language
      description
      requirements
      functionName
      testCases
      hints
      starterCode
      solution
    }
  }
`;

export const UPDATE_CODING_PROBLEM = gql`
  mutation UpdateCodingProblem($input: UpdateCodingProblemInput!) {
    updateCodingProblem(input: $input) {
      id
      title
      difficulty
      language
      description
      requirements
      functionName
      inputSpec
      outputSpec
      testCases
      hints
      starterCode
      solution
    }
  }
`;

// Vector Search Queries
export const SEARCH_ALL = gql`
  query Search($input: SearchInput!) {
    search(input: $input) {
      type
      id
      title
      description
      similarity
      slug
      company
      companySlug
      location
      courseId
      courseSlug
      lessonNumber
    }
  }
`;

export const SEARCH_COURSES = gql`
  query SearchCourses($query: String!, $limit: Int) {
    searchCourses(query: $query, limit: $limit) {
      type
      id
      title
      description
      similarity
      slug
    }
  }
`;

export const SEARCH_LESSONS = gql`
  query SearchLessons($query: String!, $limit: Int) {
    searchLessons(query: $query, limit: $limit) {
      type
      id
      title
      description
      similarity
      courseId
    }
  }
`;

export const SEARCH_JOBS = gql`
  query SearchJobs($query: String!, $limit: Int) {
    searchJobs(query: $query, limit: $limit) {
      type
      id
      title
      description
      similarity
      company
      location
    }
  }
`;

// Job Favorites
export const GET_MY_FAVORITE_JOBS = gql`
  query GetMyFavoriteJobs {
    myFavoriteJobs
  }
`;

export const IS_JOB_FAVORITED = gql`
  query IsJobFavorited($jobId: Int!) {
    isJobFavorited(jobId: $jobId)
  }
`;

export const ADD_JOB_FAVORITE = gql`
  mutation AddJobFavorite($jobId: Int!) {
    addJobFavorite(jobId: $jobId) {
      id
      userId
      jobId
      createdAt
    }
  }
`;

export const REMOVE_JOB_FAVORITE = gql`
  mutation RemoveJobFavorite($jobId: Int!) {
    removeJobFavorite(jobId: $jobId)
  }
`;

// SQL Exercise
export const GET_SQL_EXERCISE_BY_LESSON = gql`
  query GetSqlExerciseByLessonId($lessonId: String!) {
    sqlExerciseByLessonId(lessonId: $lessonId) {
      id
      lessonId
      initialSchema
      initialQuery
      expectedResult
      expectedQuery
      hiddenSchema
      hiddenExpectedResult
      hints
      instructions
    }
  }
`;

export const UPDATE_SQL_EXERCISE_BY_LESSON = gql`
  mutation UpdateSqlExerciseByLesson(
    $lessonId: String!
    $input: UpdateSqlExerciseInput!
  ) {
    updateSqlExerciseByLessonId(lessonId: $lessonId, input: $input) {
      id
      lessonId
      initialSchema
      initialQuery
      expectedResult
      expectedQuery
      hints
      instructions
    }
  }
`;

// Quiz Questions
export const GET_QUESTIONS_BY_LESSON = gql`
  query GetQuestionsByLesson($lessonId: String!) {
    questionsByLessonId(lessonId: $lessonId) {
      id
      courseId
      lessonId
      questionType
      questionText
      questionHtml
      difficulty
      points
      timeLimit
      orderIndex
      isActive
      tags
      choices {
        id
        questionId
        choiceText
        choiceHtml
        orderIndex
      }
    }
  }
`;

// Admin editor only. The API protects every answer-bearing field as well;
// keeping a separate document prevents learner loaders and caches from ever
// receiving an answer key in the first place.
export const GET_ADMIN_QUESTIONS_BY_LESSON = gql`
  query GetAdminQuestionsByLesson($lessonId: String!) {
    questionsByLessonId(lessonId: $lessonId) {
      id
      courseId
      lessonId
      questionType
      questionText
      questionHtml
      explanation
      explanationHtml
      difficulty
      points
      timeLimit
      orderIndex
      isActive
      tags
      choices {
        id
        questionId
        choiceText
        choiceHtml
        isCorrect
        orderIndex
        explanation
      }
    }
  }
`;

// Reset Code Progress
export const RESET_CODE_PROGRESS = gql`
  mutation ResetCodeProgress($lessonId: String!, $fileId: String!) {
    resetCodeProgress(lessonId: $lessonId, fileId: $fileId) {
      success
      id
      message
    }
  }
`;

// User Course Progress
export const GET_USER_COURSES_PROGRESS = gql`
  query GetUserCoursesProgress {
    getUserCoursesProgress {
      courseId
      completedLessons
      totalLessons
      progressPercentage
    }
  }
`;

// Quiz Question Mutations
export const UPDATE_QUIZ_QUESTIONS = gql`
  mutation UpdateQuizQuestions($input: UpdateQuizQuestionsInput!) {
    updateQuizQuestions(input: $input) {
      id
      questionText
      explanation
      points
      choices {
        id
        choiceText
        isCorrect
        orderIndex
      }
    }
  }
`;

// ============================================================================
// 質問ひろば
// ============================================================================

export const GET_COMMUNITY_QUESTIONS = gql`
  query GetCommunityQuestions(
    $status: String
    $courseSlug: String
    $lessonSlug: String
    $userId: String
    $query: String
    $limit: Int
    $offset: Int
  ) {
    communityQuestions(
      status: $status
      courseSlug: $courseSlug
      lessonSlug: $lessonSlug
      userId: $userId
      query: $query
      limit: $limit
      offset: $offset
    ) {
      total
      questions {
        id
        userId
        authorName
        title
        bodyMd
        courseSlug
        courseTitle
        lessonSlug
        lessonTitle
        status
        isPublic
        likeCount
        answerCount
        createdAt
      }
    }
  }
`;

export const GET_COMMUNITY_QUESTION = gql`
  query GetCommunityQuestion($id: String!) {
    communityQuestion(id: $id) {
      question {
        id
        userId
        authorName
        title
        bodyMd
        courseSlug
        courseTitle
        lessonSlug
        lessonTitle
        attachedCode
        attachedError
        expected
        actual
        tried
        status
        likeCount
        answerCount
        createdAt
      }
      answers {
        id
        questionId
        userId
        authorName
        bodyMd
        isStaff
        isAccepted
        likeCount
        createdAt
      }
      questionComments {
        id
        parentType
        parentId
        userId
        authorName
        body
        createdAt
      }
      answerComments {
        answerId
        comments {
          id
          parentType
          parentId
          userId
          authorName
          body
          createdAt
        }
      }
    }
  }
`;

export const CREATE_COMMUNITY_QUESTION = gql`
  mutation CreateCommunityQuestion($input: CreateCommunityQuestionInput!) {
    createCommunityQuestion(input: $input) {
      id
      title
      status
    }
  }
`;

export const CREATE_COMMUNITY_ANSWER = gql`
  mutation CreateCommunityAnswer($questionId: String!, $bodyMd: String!) {
    createCommunityAnswer(questionId: $questionId, bodyMd: $bodyMd) {
      id
      questionId
      isStaff
    }
  }
`;

export const ACCEPT_COMMUNITY_ANSWER = gql`
  mutation AcceptCommunityAnswer($questionId: String!, $answerId: String!) {
    acceptCommunityAnswer(questionId: $questionId, answerId: $answerId)
  }
`;

export const CREATE_COMMUNITY_COMMENT = gql`
  mutation CreateCommunityComment(
    $parentType: String!
    $parentId: String!
    $body: String!
  ) {
    createCommunityComment(
      parentType: $parentType
      parentId: $parentId
      body: $body
    ) {
      id
      parentId
    }
  }
`;
